import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { CrmAssociationService } from '../../../shared-services/crm-services/crm-object-service/crm-association.service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateModule } from '@ngx-translate/core';
import { EditAssociationTagsComponent } from '../edit-association-tags/edit-association-tags.component';
import { map, switchMap } from 'rxjs';
import { PRIMARY_TAG, RESERVED_TAGS, SNACKBAR_DURATION } from '../../../constants';

export interface EditAssociationData {
  associationId: string;
  associationName: string;
}

@Component({
  selector: 'crm-edit-association-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatDialogModule, MatButtonModule, EditAssociationTagsComponent],
  templateUrl: './edit-association-dialog.component.html',
  styleUrls: ['./edit-association-dialog.component.scss'],
})
export class EditAssociationDialogComponent {
  private initialAssociation$ = this.crmAssociationService.getAssociation(this.data.associationId);
  private isPrimary$ = this.initialAssociation$.pipe(
    map((association) => (association?.tags ?? []).map((tag) => tag?.tagValue ?? '').filter((tag) => tag)),
    map((tags) => tags.includes(PRIMARY_TAG)),
  );
  initialTags$ = this.initialAssociation$.pipe(
    map((association) => (association?.tags ?? []).map((tag) => tag?.tagValue ?? '').filter((tag) => tag)),
    map((tags) => tags.filter((t) => !RESERVED_TAGS.includes(t.toLowerCase()))),
  );
  associationType$ = this.initialAssociation$.pipe(map((association) => association?.associationType ?? ''));
  tagsToSave: string[] = [];

  constructor(
    public dialogRef: MatDialogRef<EditAssociationDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: EditAssociationData,
    private readonly crmAssociationService: CrmAssociationService,
    private readonly snackService: SnackbarService,
  ) {}

  save(): void {
    this.isPrimary$
      .pipe(
        switchMap((isPrimary) => {
          let tagsToSave = [...this.tagsToSave];
          if (isPrimary) {
            tagsToSave = [PRIMARY_TAG, ...tagsToSave];
          }
          return this.crmAssociationService.updateAssociationTags(this.data.associationId, tagsToSave);
        }),
      )
      .subscribe({
        next: () => {
          this.snackService.openSuccessSnack('ASSOCIATIONS.EDIT_ASSOCIATION_DIALOG.SUCCESS', {
            duration: SNACKBAR_DURATION,
          });
          this.dialogRef.close(true);
        },
        error: () => {
          this.snackService.openErrorSnack('ASSOCIATIONS.EDIT_ASSOCIATION_DIALOG.ERROR', {
            duration: SNACKBAR_DURATION,
          });
        },
      });
  }

  cancel(): void {
    this.dialogRef?.close();
  }

  tagsChanged(tags: string[]): void {
    this.tagsToSave = (tags || []).slice();
  }
}
